import { TrackingConversionType } from '@/utils/tracking-conversion-types';

/**
 * Single source of truth mapping each internal `TrackingConversionType` (UPPER_SNAKE, the value
 * written to the internal tracking DB) to its standardized snake_case GTM `dataLayer` event name.
 *
 * The internal enum and the GTM event name are intentionally separate concepts: marketing tags in
 * GTM key off the standardized names below, while the backend keeps the UPPER_SNAKE conversion
 * identifiers. Do NOT rename the enum to "fix" a mismatch — extend this map instead.
 *
 * Standardized funnel event names for the analytics team (exact strings consumed by GTM):
 *   - qualified_user
 *   - ic_start
 *   - ic_done
 *   - form_submit
 *   - initiate_payment
 *   - feedback_submit
 *   - tile_click
 *   - program_start
 *   - verification_start
 */
export const DATA_LAYER_EVENT_NAMES: Partial<Record<TrackingConversionType, string>> = {
  [TrackingConversionType.QUALIFIED_USER]: 'qualified_user',
  [TrackingConversionType.IC_START]: 'ic_start',
  [TrackingConversionType.IC_DONE]: 'ic_done',
  [TrackingConversionType.FORM_SUBMIT]: 'form_submit',
  [TrackingConversionType.INITIATE_PAYMENT]: 'initiate_payment',
  [TrackingConversionType.FEEDBACK_SUBMIT]: 'feedback_submit',
  [TrackingConversionType.TILE_CLICK]: 'tile_click',
  [TrackingConversionType.PROGRAM_START]: 'program_start',
  [TrackingConversionType.VERIFICATION_START]: 'verification_start',
};

/**
 * Resolve the standardized GTM event name for a conversion. Accepts either a `TrackingConversionType`
 * or a raw string. Falls back to the provided value when no mapping exists, preserving the previous
 * behaviour for conversions that intentionally push their raw identifier (e.g. SERVICE_ACTIVATED).
 */
export function resolveDataLayerEventName(conversion: TrackingConversionType | string): string {
  return DATA_LAYER_EVENT_NAMES[conversion as TrackingConversionType] ?? conversion;
}
